/**
 * Reference layers a land professional wants under a parcel, toggled on.
 *
 * The PLSS one is not decoration. It is **the same fabric the server resolves
 * aliquot verdicts against** (`groundtruth_core/plss.py` queries
 * `BLM_Natl_PLSS_CadNSDI/MapServer/2`), so an operator placing a metes-and-
 * bounds tract that recites "Section 6, T17S R18E" can see whether it landed
 * in that section. That is a check the arithmetic cannot make, in the same
 * way the certificate's drawn figure is: closure and acreage say the shape is
 * right, and only a human comparing it against the grid says the position is.
 *
 * Served as dynamic export images rather than cached tiles, because the BLM
 * service is a MapServer with no tile cache. `{bbox-epsg-3857}` is MapLibre's
 * own substitution for WMS-style sources and ArcGIS `export` accepts the same
 * shape, so no proxy is involved and no key is needed.
 *
 * **Why the grid used to come and go.** Both BLM layers carry a `minScale`:
 * townships draw below about 1:4,000,000 and sections below 1:500,000.
 * Outside those the service answers 200 with a fully transparent image, so
 * every request succeeds and nothing appears, which reads as "the layer loads
 * intermittently" and is really "you are zoomed too far out". `minzoom` stops
 * us asking, and the map says so instead of leaving the user to guess.
 *
 * Tiles are 512px rather than 256 for the same reason a dynamic service
 * deserves consideration: it quarters the number of `export` requests a pan
 * costs, and this one renders each on demand.
 */

const BLM =
  "https://gis.blm.gov/arcgis/rest/services/Cadastral/BLM_Natl_PLSS_CadNSDI/MapServer/export";

function blmExport(layers: string): string {
  const params = new URLSearchParams({
    bbox: "{bbox-epsg-3857}",
    bboxSR: "3857",
    imageSR: "3857",
    size: "512,512",
    format: "png32",
    transparent: "true",
    dpi: "96",
    layers: `show:${layers}`,
    f: "image",
  });
  // URLSearchParams percent-encodes the braces, and MapLibre needs them
  // literal to substitute the bounding box.
  return `${BLM}?${params.toString()}`.replace(
    "%7Bbbox-epsg-3857%7D",
    "{bbox-epsg-3857}",
  );
}

export interface Overlay {
  id: string;
  label: string;
  title: string;
  tiles: string[];
  attribution: string;
  tileSize: number;
  /** Below this the service draws nothing, so asking is pure waste. */
  minzoom: number;
  /** What to tell someone who turned it on too far out. */
  hint: string;
  /** Where the grid exists at all, stated so a blank map reads as coverage
   *  rather than as a fault. Texas ran on Spanish and Mexican grants and then
   *  its own General Land Office surveys, and the colonial states used metes
   *  and bounds from the start; both are empty here. Worded to stay true
   *  wherever the viewer happens to be looking. */
  coverage: string;
}

export const OVERLAYS: Overlay[] = [
  {
    id: "plss-sections",
    label: "PLSS sections",
    title:
      "Township, range and section lines from the BLM CadNSDI fabric, the same survey grid Easting resolves aliquot descriptions against. Covers the 30 public-land states; Texas and the colonial states were never surveyed this way and have nothing to show.",
    // 1 is Township, 2 is Section. Both, so the grid reads at either zoom.
    tiles: [blmExport("1,2")],
    attribution:
      '<a href="https://gis.blm.gov/arcgis/rest/services/Cadastral/BLM_Natl_PLSS_CadNSDI/MapServer">BLM PLSS CadNSDI</a>',
    tileSize: 512,
    // 1:4,000,000 is about zoom 7 at 96 dpi (559082264 / 2^z), which is where
    // townships start drawing. Sections join at about zoom 10.
    minzoom: 7,
    hint: "Zoom in to see the PLSS grid: townships appear at county scale, sections a little closer.",
    coverage:
      "PLSS covers the 30 public-land states. Texas and the colonial states were never surveyed on this grid, so they are blank.",
  },
];
